import React, { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Check, Plus, X } from 'lucide-react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { axiosGetAPI, axiosPost } from 'services';
import { CITY_BY_STATE } from 'pages/MarketListing/MarketListingGraphQL';

const formatLocationLabel = (cityName, stateName) =>
  `${cityName || ''}${stateName ? `(${stateName})` : ''}`;

const ServiceableLocationsModal = ({
  isOpen,
  city,
  states,
  auth,
  onSaved,
  onClose,
}) => {
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedCount, setSelectedCount] = useState(0);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [fetchCitiesByState] = useLazyQuery(CITY_BY_STATE, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!isOpen) return undefined;

    let isMounted = true;
    setSelectedLocations([]);
    setSelectedCount(0);
    setSearch('');
    setLoadError(false);
    setIsSaving(false);
    setIsLoading(true);

    const loadLocations = async () => {
      try {
        const [citiesByState, savedLocations] = await Promise.all([
          Promise.all(
            states.map(async (state) => {
              const response = await fetchCitiesByState({
                variables: { state: String(state.id) },
              });

              return (response?.data?.citiesByState || []).map(
                (cityOption) => ({
                  cityId: String(cityOption.id),
                  cityName: cityOption.cityName,
                  stateId: String(state.id),
                  stateName: state.stateName,
                  label: formatLocationLabel(
                    cityOption.cityName,
                    state.stateName,
                  ),
                }),
              );
            }),
          ),
          axiosGetAPI(
            `${process.env.REACT_APP_REST_API_ENDPOINT}common/serviceable-locations/${city.id}`,
            auth,
          ),
        ]);

        if (!savedLocations?.isSuccessful || savedLocations?.data?.error) {
          throw new Error(
            savedLocations?.data?.message ||
              savedLocations?.message ||
              'Unable to load saved serviceable locations.',
          );
        }

        const uniqueLocations = citiesByState
          .flat()
          .filter(
            (location, index, allLocations) =>
              allLocations.findIndex(
                (item) => item.cityId === location.cityId,
              ) === index,
          )
          .sort((first, second) => first.label.localeCompare(second.label));

        const savedData = savedLocations?.data?.data;
        const locationsById = new Map(
          uniqueLocations.map((location) => [location.cityId, location]),
        );
        const savedCityIds =
          savedData?.destinationCityIds ||
          savedData?.destinations?.map((destination) => destination.cityId) ||
          [];
        const savedDestinations = (savedData?.destinations || []).map(
          (destination) => ({
            cityId: String(destination.cityId),
            cityName: destination.cityName,
            stateId: String(destination.stateId || ''),
            stateName: destination.stateName,
            label: formatLocationLabel(
              destination.cityName,
              destination.stateName,
            ),
          }),
        );
        const initialSelection = savedCityIds
          .map((id) =>
            locationsById.get(String(id)) ||
              savedDestinations.find(
                (destination) => destination.cityId === String(id),
              ),
          )
          .filter(Boolean);
        const savedCount = Number(savedData?.selectedCount);

        if (isMounted) {
          setLocations(uniqueLocations);
          setSelectedLocations(initialSelection);
          setSelectedCount(
            Number.isFinite(savedCount) ? savedCount : initialSelection.length,
          );
        }
      } catch (error) {
        if (isMounted) {
          setLoadError(true);
          toast.error(error?.message || 'Unable to load serviceable locations.');
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadLocations();

    return () => {
      isMounted = false;
    };
  }, [auth, city?.id, fetchCitiesByState, isOpen, states]);

  const filteredLocations = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    if (!searchTerm) return locations;

    return locations.filter((location) =>
      location.label.toLowerCase().includes(searchTerm),
    );
  }, [locations, search]);

  const selectedIds = useMemo(
    () => new Set(selectedLocations.map((location) => location.cityId)),
    [selectedLocations],
  );

  const toggleLocation = (location) => {
    if (isSaving || isLoading || loadError) return;

    const isSelected = selectedLocations.some(
      (item) => item.cityId === location.cityId,
    );
    const nextSelection = isSelected
      ? selectedLocations.filter((item) => item.cityId !== location.cityId)
      : [...selectedLocations, location].sort((first, second) =>
          first.label.localeCompare(second.label),
        );
    setSelectedLocations(nextSelection);
    setSelectedCount(nextSelection.length);
  };

  const closeModal = () => {
    if (!isSaving) onClose();
  };

  const handleSave = async () => {
    if (!city?.id || isLoading || loadError || isSaving) return;

    setIsSaving(true);
    try {
      const response = await axiosPost(
        `${process.env.REACT_APP_REST_API_ENDPOINT}common/serviceable-locations`,
        {
          sourceCityId: Number(city.id),
          destinationCityIds: selectedLocations.map((location) =>
            Number(location.cityId),
          ),
        },
        { 'Content-Type': 'application/json' },
        auth,
      );

      if (!response?.isSuccessful || response?.data?.error) {
        toast.error(
          response?.data?.message ||
            response?.message ||
            'Unable to save serviceable locations.',
        );
        return;
      }

      toast.success(
        response?.data?.message || 'Serviceable locations saved successfully.',
      );
      const savedCount = Number(response?.data?.data?.selectedCount);
      const nextCount = Number.isFinite(savedCount)
        ? savedCount
        : selectedLocations.length;
      setSelectedCount(nextCount);
      onSaved?.(city.id, nextCount);
      onClose();
    } catch (error) {
      toast.error(error?.message || 'Unable to save serviceable locations.');
    } finally {
      setIsSaving(false);
    }
  };

  let saveNote = '';
  if (loadError) {
    saveNote = 'Saved locations could not be loaded. Close and reopen to try again.';
  } else if (isSaving) {
    saveNote = 'Saving serviceable locations...';
  }

  return (
    <Modal
      centered
      className="city-master-serviceable-modal"
      isOpen={isOpen}
      toggle={closeModal}
    >
      <ModalHeader toggle={closeModal}>
        <span className="city-master-serviceable-title">
          <span>
            Serviceable Location{city?.cityName ? ` - ${city.cityName}` : ''}
          </span>
          <span className="city-master-serviceable-title-count">
            {selectedCount}
          </span>
        </span>
      </ModalHeader>
      <ModalBody>
        <div className="city-master-location-columns">
          <section className="city-master-location-panel" aria-label="Locations">
            <div className="city-master-location-panel-heading">
              <h3>Location</h3>
              <span>{filteredLocations.length}</span>
            </div>
            <Input
              aria-label="Search locations"
              className="city-master-location-search"
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search locations"
              type="search"
              value={search}
            />
            <div
              className="city-master-location-grid city-master-location-options"
              aria-live="polite"
            >
              {isLoading && (
                <div className="city-master-location-message">
                  Loading locations...
                </div>
              )}
              {!isLoading && loadError && (
                <div className="city-master-location-message is-error">
                  Unable to load locations.
                </div>
              )}
              {!isLoading && !loadError && filteredLocations.length === 0 && (
                <div className="city-master-location-message">
                  {search.trim()
                    ? 'No locations match your search.'
                    : 'No locations available.'}
                </div>
              )}
              {!isLoading &&
                !loadError &&
                filteredLocations.map((location) => {
                  const isSelected = selectedIds.has(location.cityId);

                  return (
                    <button
                      aria-pressed={isSelected}
                      className={`city-master-location-card${
                        isSelected ? ' is-selected' : ''
                      }`}
                      disabled={isLoading || loadError || isSaving}
                      key={location.cityId}
                      onClick={() => toggleLocation(location)}
                      title={location.label}
                      type="button"
                    >
                      <span>{location.label}</span>
                      {isSelected ? (
                        <Check aria-hidden="true" size={15} />
                      ) : (
                        <Plus aria-hidden="true" size={15} />
                      )}
                    </button>
                  );
                })}
            </div>
          </section>

          <section
            className="city-master-location-panel city-master-selected-panel"
            aria-label="Selected locations"
          >
            <div className="city-master-location-panel-heading">
              <h3>Selected Location</h3>
              <span>{selectedLocations.length}</span>
            </div>
            <div className="city-master-location-grid selected-grid">
              {selectedLocations.length === 0 ? (
                <div className="city-master-location-message">
                  Select locations to add them here.
                </div>
              ) : (
                selectedLocations.map((location) => (
                  <button
                    aria-label={`Remove ${location.label}`}
                    className="city-master-location-card is-selected"
                    disabled={isLoading || loadError || isSaving}
                    key={location.cityId}
                    onClick={() => toggleLocation(location)}
                    title={location.label}
                    type="button"
                  >
                    <span>{location.label}</span>
                    <X aria-hidden="true" size={15} />
                  </button>
                ))
              )}
            </div>
          </section>
        </div>
      </ModalBody>
      <ModalFooter>
        <span className="city-master-location-save-note">
          {saveNote}
        </span>
        <Button
          color="secondary"
          disabled={isSaving}
          onClick={closeModal}
          outline
          type="button"
        >
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={isLoading || loadError || isSaving}
          onClick={handleSave}
          type="button"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ServiceableLocationsModal;
